import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  TextField,
  Chip,
  IconButton,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Search, Clear, Add, Edit, Visibility } from "@mui/icons-material";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { getFacilities } from "service/admin/facilityService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import FacilityForm from "./FacilityForm";

const Facilities = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [bookableFilter, setBookableFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
        search: search || undefined,
      };
      if (bookableFilter !== "all") params.is_bookable = bookableFilter === "bookable";
      if (statusFilter !== "all") params.registration_status = statusFilter;

      const response = await getFacilities(params);
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load facilities.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, bookableFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const statusChip = (status) => {
    const map = {
      active: { label: "Active", color: "success" },
      pending: { label: "Pending", color: "warning" },
      suspended: { label: "Suspended", color: "error" },
    };
    const cfg = map[status] || { label: status || "-", color: "default" };
    return <Chip label={cfg.label} size="small" color={cfg.color} />;
  };

  const columns = [
    {
      field: "name",
      headerName: "NAME",
      flex: 1.5,
    },
    {
      field: "category",
      headerName: "CATEGORY",
      flex: 1,
      renderCell: (params) =>
        params.value ? params.value.replace(/_/g, " ").toUpperCase() : "-",
    },
    {
      field: "city",
      headerName: "CITY",
      flex: 1,
      renderCell: (params) => params.row.address || "-",
    },
    {
      field: "owner",
      headerName: "OWNER",
      flex: 1,
      renderCell: (params) =>
        params.row.owner?.full_name || params.row.owner_name || "Unassigned",
    },
    {
      field: "registration_status",
      headerName: "STATUS",
      flex: 0.9,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "is_bookable",
      headerName: "BOOKABLE",
      flex: 0.8,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Bookable" size="small" color="secondary" />
        ) : (
          <Chip label="Listing" size="small" variant="outlined" />
        ),
    },
    {
      field: "is_strikie_owned",
      headerName: "STRIKIE",
      flex: 0.7,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Strikie" size="small" color="primary" />
        ) : null,
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => navigate(`/facilities/${params.row.id}`)}
            title="View"
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
            }}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween>
          <Header title="FACILITIES" subTitle="Manage all facilities" />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => setFormOpen(true)}
          >
            Add Facility
          </Button>
        </FlexBetween>

        <Box mt="1rem" mb="0.5rem" display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search facilities..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            color="secondary"
            sx={{ width: 260 }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton onClick={handleSearch} color="secondary">
            <Search />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel color="secondary">Bookable</InputLabel>
            <Select
              color="secondary"
              label="Bookable"
              value={bookableFilter}
              onChange={(e) => { setBookableFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="bookable">Bookable</MenuItem>
              <MenuItem value="listing">Listing</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel color="secondary">Status</InputLabel>
            <Select
              color="secondary"
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          mt="10px"
          height="68vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
            onRowClick={(params) => navigate(`/facilities/${params.row.id}`)}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              cursor: "pointer",
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>

      <FacilityForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={fetchData}
      />
    </>
  );
};

export default Facilities;
